import { type SessionStatus } from '@heidi/schema/ml-scribe';
import { useQuery } from '@tanstack/react-query';

import {
  getSessionCountBySessionStatus,
  getTeamMemberSessionCountBySessionStatus,
} from '../api/list';
import { getSessionDataConfig } from '../config';
import {
  GET_SESSION_STATUS_COUNT,
  GET_TEAM_MEMBER_SESSION_STATUS_COUNT,
} from '../query/sessionStatusCountKeys';

export interface UseQuerySessionCountBySessionStatusOptions {
  /** Gate the query independently of its own readiness checks. Defaults to
   *  on — the All Sessions scope passes `false` so its single whole-scope
   *  call (`useQueryAllSessionsStatusCounts`) replaces this per-status one
   *  instead of firing alongside it. */
  enabled?: boolean;
}

export function useQuerySessionCountBySessionStatus(
  status: SessionStatus,
  options: UseQuerySessionCountBySessionStatusOptions = {}
) {
  const {
    teamId,
    selectedReviewMemberId,
    currentUserId,
    canReviewOtherUsersSessionList,
  } = getSessionDataConfig().useSessionStatusCountReviewContext();

  const teamMemberQuery = useQueryTeamMemberSessionCountBySessionStatus(status, options);
  const adminQuery = useQueryAdminSessionCountBySessionStatus(status, options);

  if (
    teamId &&
    selectedReviewMemberId &&
    selectedReviewMemberId !== currentUserId &&
    canReviewOtherUsersSessionList
  ) {
    return teamMemberQuery;
  } else {
    return adminQuery;
  }
}

function useQueryAdminSessionCountBySessionStatus(
  status: SessionStatus,
  options: UseQuerySessionCountBySessionStatusOptions
) {
  const token = getSessionDataConfig().useAuthToken?.();
  const { currentUserId } = getSessionDataConfig().useSessionStatusCountReviewContext();

  return useQuery({
    queryKey: [GET_SESSION_STATUS_COUNT, status],
    queryFn: async () => {
      const { data } = await getSessionCountBySessionStatus(status);
      return data;
    },
    enabled: Boolean(token && currentUserId && (options.enabled ?? true)),
  });
}

function useQueryTeamMemberSessionCountBySessionStatus(
  status: SessionStatus,
  options: UseQuerySessionCountBySessionStatusOptions
) {
  const token = getSessionDataConfig().useAuthToken?.();
  const {
    teamId,
    selectedReviewMemberId,
    canReviewOtherUsersSessionList,
    isReviewingOtherUser,
  } = getSessionDataConfig().useSessionStatusCountReviewContext();

  const memberId = selectedReviewMemberId;

  return useQuery({
    queryKey: [GET_TEAM_MEMBER_SESSION_STATUS_COUNT, status, { teamId }, { memberId }],
    queryFn: async () => {
      if (teamId && memberId) {
        const { data } = await getTeamMemberSessionCountBySessionStatus({
          teamId,
          memberId,
          sessionStatus: status,
        });
        return data;
      }
    },
    enabled: Boolean(
      token &&
      teamId &&
      memberId &&
      canReviewOtherUsersSessionList &&
      isReviewingOtherUser &&
      (options.enabled ?? true)
    ),
  });
}
